import { apiClient } from "./api-client";
import type { AzureLoginRequest, BasicLoginRequest } from "./api-types";
import type { LoginResponse, TriyoUser } from "@/types/models";

export type CurrentUserResponse = {
  User: TriyoUser;
  Token?: string;
  AccessToken?: string;
  GraphToken?: string;
};

// TRIYO authentication endpoints (mirrors legacy Triyo.Addin.Web ApiService).
export const AuthApi = {
  login: async (request: BasicLoginRequest): Promise<LoginResponse> => {
    const res = await apiClient.post<LoginResponse>("/users/Login", request);
    return res.data;
  },

  azureLogin: async (request: AzureLoginRequest): Promise<LoginResponse> => {
    const res = await apiClient.post<LoginResponse>("/users/AzureLogin", request);
    return res.data;
  },

  getCurrentUser: async (): Promise<CurrentUserResponse> => {
    const res = await apiClient.get<CurrentUserResponse>("/users/GetCurrentUser");
    return res.data;
  },

  /**
   * Validates a web-app SSO cookie/token against the API (legacy Login.js ssoLogin).
   * Uses an explicit Bearer header so it does not depend on localStorage yet.
   */
  getCurrentUserWithToken: async (token: string): Promise<CurrentUserResponse> => {
    const baseURL = window.APP_SETTINGS?.apiUrl ?? "/api";
    const res = await fetch(`${baseURL}/users/GetCurrentUser`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) {
      throw new Error("Could not validate the TRIYO web session. Please sign in again.");
    }
    return (await res.json()) as CurrentUserResponse;
  },

  /**
   * Optional API helper used by some environments (legacy ApiService.loginSSO).
   * Prefer getCurrentUserWithToken for the cookie-based web SSO flow.
   */
  loginSso: async (timezone: number): Promise<LoginResponse> => {
    const res = await apiClient.get<LoginResponse>(`/users/LoginSSO?timezone=${timezone}`, {
      withCredentials: true,
    });
    return res.data;
  },

  logout: async (): Promise<void> => {
    await apiClient.post("/users/Logout", {});
  },
};
